import React, { useCallback } from "react";
import Highcharts from "highcharts";
import Color from "color";
import { color } from "@ui";
import { zIndexChart } from "@ui/Highcharts/BetterHighcharts";
import t from "../../../../i18n";
import { WEEK_MS } from "@ui/i18n/formatDate";

const POST_ANALYSIS_PLOT_BAND_ID = "post-latest-analysis-plotband";
const POST_ANALYSIS_DEBUG_LABEL_ID = "post-latest-analysis-debug-label";
const POST_ANALYSIS_NOW_LABEL_ID = "post-latest-analysis-now-label";
const AVERAGE_CHARACTER_WIDTH_PX = 7;
const postLatestAnalysisLabelByKey = {
  "chart.period.now": t("chart.period.now"),
  "chart.period.no_analysis_yet": t("chart.period.no_analysis_yet"),
  "chart.period.no_data_yet": t("chart.period.no_data_yet"),
} as const;
export const postLatestAnalysisPlotBandColor = new Color(color.raw.titanium100)
  .alpha(0.6)
  .toString();
export const postLatestAnalysisNavigatorPlotBandColor = new Color(color.raw.titanium100)
  .alpha(0.75)
  .toString();

function getLatestAnalysisDate(analyses: { date: number }[]) {
  if (analyses.length === 0) return null;
  const [firstAnalysis, ...otherAnalyses] = analyses;
  if (!firstAnalysis) return null;
  return otherAnalyses.reduce(
    (latest, analysis) => Math.max(latest, analysis.date),
    firstAnalysis.date,
  );
}

function getAxis(chart: Highcharts.Chart, navigator: boolean) {
  const axis = chart.xAxis[navigator ? 1 : 0];
  if (!axis && navigator) return null;
  if (!axis) throw new Error(`Missing xAxis ${navigator ? 1 : 0}`);
  return axis;
}

const usePostLatestAnalysisPlotBand = ({
  chartRef,
  analyses,
  latestDate,
  showLabel = true,
  labelTextKey = "chart.period.no_analysis_yet",
}: {
  chartRef: React.RefObject<Highcharts.Chart>;
  analyses?: { date: number }[];
  latestDate?: number | null;
  showLabel?: boolean;
  labelTextKey?: "chart.period.no_analysis_yet" | "chart.period.no_data_yet";
}) => {
  const latestAnalysisDate = React.useMemo(
    () => latestDate ?? getLatestAnalysisDate(analyses ?? []),
    [analyses, latestDate],
  );

  const renderLastAnalysisPlotBlandsAndLines = useCallback(() => {
    const chart = chartRef.current;
    if (!chart || latestAnalysisDate === null) return;

    const now = Date.now();
    const xAxis = getAxis(chart, false);
    const navigatorAxis = getAxis(chart, true);
    if (!xAxis) throw new Error("Missing xAxis 0");
    const { min, max } = xAxis.getExtremes();
    const plotBandRightBoundary = Math.max(now, max ?? now);
    const visibleRightBoundary = max ?? now;

    if (latestAnalysisDate >= plotBandRightBoundary) return;

    xAxis.removePlotBand(POST_ANALYSIS_PLOT_BAND_ID);
    xAxis.removePlotLine(POST_ANALYSIS_DEBUG_LABEL_ID);
    xAxis.removePlotLine(POST_ANALYSIS_NOW_LABEL_ID);
    navigatorAxis?.removePlotBand(POST_ANALYSIS_PLOT_BAND_ID);
    navigatorAxis?.removePlotLine(POST_ANALYSIS_DEBUG_LABEL_ID);
    navigatorAxis?.removePlotLine(POST_ANALYSIS_NOW_LABEL_ID);

    const labelText = postLatestAnalysisLabelByKey[labelTextKey];
    const visibleFrom = Math.max(latestAnalysisDate, min ?? latestAnalysisDate);
    const visibleWidthPx = Math.max(
      0,
      xAxis.toPixels(visibleRightBoundary, true) - xAxis.toPixels(visibleFrom, true),
    );
    const minimumLabelWidthPx = labelText.length * AVERAGE_CHARACTER_WIDTH_PX;
    const zoneCoversWholeVisibleChart = latestAnalysisDate <= (min ?? latestAnalysisDate);
    const labelAnchorValue = (visibleFrom + visibleRightBoundary) / 2;
    const labelOffsetY = Math.round(chart.plotHeight / 3);

    const postAnalysisPlotBand = {
      id: POST_ANALYSIS_PLOT_BAND_ID,
      from: latestAnalysisDate,
      to: plotBandRightBoundary,
      color: postLatestAnalysisPlotBandColor,
      zIndex: zIndexChart.plotBands,
    };
    xAxis.addPlotBand(postAnalysisPlotBand);
    navigatorAxis?.addPlotBand({
      ...postAnalysisPlotBand,
      color: postLatestAnalysisNavigatorPlotBandColor,
    });

    if (showLabel && !zoneCoversWholeVisibleChart && visibleWidthPx >= minimumLabelWidthPx) {
      xAxis.addPlotLine({
        id: POST_ANALYSIS_DEBUG_LABEL_ID,
        value: labelAnchorValue,
        width: 0,
        color: "transparent",
        zIndex: zIndexChart.plotLines,
        label: {
          text: labelText,
          align: "center",
          verticalAlign: "top",
          y: labelOffsetY,
          rotation: 0,
          style: {
            color: color.carbon600,
            fontSize: "11px",
            fontWeight: "500",
            textOutline: "none",
          },
        },
      });
    }
  }, [chartRef, labelTextKey, latestAnalysisDate, showLabel]);

  React.useLayoutEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const now = Date.now();

    chart.update({
      xAxis: {
        type: "datetime",
        max: now,
        tickPositioner() {
          const positions = this.tickPositions ?? [];
          const t = this.max ?? now;
          const l = positions.at(-1) ?? Infinity;
          if (t > l + WEEK_MS) positions.push(t);
          else if (t > l) positions.splice(positions.length - 1, 1, t);
          return positions;
        },
      },
    });

    Highcharts.addEvent(chart, "render", renderLastAnalysisPlotBlandsAndLines);
    return () => Highcharts.removeEvent(chart, "render", renderLastAnalysisPlotBlandsAndLines);
  }, [chartRef, renderLastAnalysisPlotBlandsAndLines]);
};

export default usePostLatestAnalysisPlotBand;
